import { requestServices } from "@/utils/request";
import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import { EngineKeys } from "@/app-search/services/request-app-search.service";
import { ACCESS_TICKET_TYPES } from "@/constants/check-in";
import { IBookingScanCount } from "@/types/check-in";
import { BookingStatus } from "@/types/db/product-slot-state";

const TICKET_INDEX = import.meta.env.VITE_ELASTIC_TICKETS;

/** One order in the aggregation answer. */
interface IBookingBucket {
  key: number;
  doc_count: number;
  checked_in?: { doc_count?: number };
}

/**
 * Count the tickets already checked in, for several orders at once.
 *
 * The engine records an entry, the ticket index carries the result as
 * `ticket_scanning_state`. One aggregation covers every visible row, so the
 * Orders table costs one call whatever the page size.
 *
 * @param {number[]} bookingIds - The orders to count.
 * @returns {Promise<IBookingScanCount[]>} One entry for each order that has
 * access tickets. An order with none is absent from the list.
 */
export async function getBookingScanCounts(
  bookingIds: number[]
): Promise<IBookingScanCount[]> {
  const token = UserModule?.getUser?.elasticSearchTokens?.[EngineKeys.Ticket];

  if (!token || !bookingIds.length) {
    return [];
  }

  const response = await requestServices({
    method: "POST",
    url: `/v1/search/${TICKET_INDEX}`,
    data: {
      query: {
        bool: {
          filter: [
            { term: { group_access: POSConfigModule.groupId } },
            { terms: { ticket_booking_id: bookingIds } },
            { term: { ticket_booking_state: BookingStatus.Confirmed } },
            { terms: { ticket_type: ACCESS_TICKET_TYPES } },
          ],
        },
      },
      size: 0,
      aggs: {
        by_booking: {
          terms: { field: "ticket_booking_id", size: bookingIds.length },
          aggs: {
            checked_in: { filter: { term: { ticket_scanning_state: 1 } } },
          },
        },
      },
    },
    headers: { Authorization: `Bearer ${token}` },
  });

  const buckets: IBookingBucket[] =
    response.data?.data?.aggregations?.by_booking?.buckets || [];

  return buckets.map((bucket) => ({
    bookingId: Number(bucket.key),
    total: Number(bucket.doc_count) || 0,
    checkedIn: Number(bucket.checked_in?.doc_count) || 0,
  }));
}
